import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const VIDEO_ITEM_SELECTOR = [
  'slideshow-slide.product-media-container--video',
  'slideshow-slide.product-media-container--external_video',
  '.media-gallery__grid > .product-media-container--video',
  '.media-gallery__grid > .product-media-container--external_video',
].join(',');

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #videoMutationObserver;
  #videoRetryObserver;
  #activeVideoItems = new Set();
  #videoScanFrame = 0;
  #firstMediaFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    if (this.#isMainProductGallery()) {
      this.#selectFirstUploadedMedia();
      this.#setupContinuousVideoPlayback();

      document.addEventListener(
        'visibilitychange',
        this.#handleVisibilityChange,
        { signal }
      );

      window.addEventListener(
        'resize',
        this.#scheduleVideoScan,
        { signal, passive: true }
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#videoMutationObserver?.disconnect();
    this.#videoRetryObserver?.disconnect();

    if (this.#videoScanFrame) {
      cancelAnimationFrame(this.#videoScanFrame);
      this.#videoScanFrame = 0;
    }

    if (this.#firstMediaFrame) {
      cancelAnimationFrame(this.#firstMediaFrame);
      this.#firstMediaFrame = 0;
    }

    this.#activeVideoItems.forEach((item) => {
      this.#pauseVideoItem(item);
    });

    this.#activeVideoItems.clear();
  }

  #isMainProductGallery() {
    return /\/products\//.test(window.location.pathname) &&
      !this.closest('dialog');
  }

  /**
   * Luôn mở thư viện tại media đầu tiên đã được tải lên.
   */
  #selectFirstUploadedMedia(attempt = 0) {
    customElements.whenDefined('slideshow-component').then(() => {
      if (!this.isConnected) return;

      this.#firstMediaFrame = requestAnimationFrame(() => {
        this.#firstMediaFrame = requestAnimationFrame(() => {
          if (!this.isConnected) return;

          const slideshow = this.slideshow;
          const scroller = slideshow?.refs?.scroller;

          if (!slideshow || !scroller) {
            if (attempt < 4) {
              this.#selectFirstUploadedMedia(attempt + 1);
            }
            return;
          }

          slideshow.removeAttribute('initial-slide');
          scroller.scrollLeft = 0;
          slideshow.select(0, undefined, { animate: false });
        });
      });
    });
  }

  /**
   * Tải và phát video ngay khi trang sản phẩm mở.
   * Không chờ khách lướt đến slide video.
   */
  #setupContinuousVideoPlayback() {
    customElements.whenDefined('deferred-media').then(() => {
      if (!this.isConnected) return;

      this.#videoMutationObserver = new MutationObserver(() => {
        this.#scheduleVideoScan();
      });

      /**
       * iPhone/Safari đôi khi tạm dừng video nằm ngoài màn hình.
       * Observer này chỉ dùng để yêu cầu phát lại khi cần,
       * không trì hoãn việc tải video ban đầu.
       */
      this.#videoRetryObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              this.#activeVideoItems.has(entry.target)
            ) {
              this.#loadAndPlayVideo(entry.target);
            }
          });
        },
        { threshold: 0.01 }
      );

      this.#videoMutationObserver.observe(this, {
        childList: true,
        subtree: true,
      });

      this.#scanAndPlayVideos();
    });
  }

  #scheduleVideoScan = () => {
    if (!this.isConnected || this.#videoScanFrame) return;

    this.#videoScanFrame = requestAnimationFrame(() => {
      this.#videoScanFrame = 0;
      this.#scanAndPlayVideos();
    });
  };

  #scanAndPlayVideos() {
    if (!this.isConnected) return;

    const nextActiveItems = new Set();

    this.querySelectorAll(VIDEO_ITEM_SELECTOR).forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      if (item.closest('media-gallery') !== this) return;
      if (item.closest('.dialog-zoomed-gallery')) return;
      if (!item.querySelector('deferred-media')) return;
      if (!this.#isRenderedLayoutItem(item)) return;

      nextActiveItems.add(item);
      this.#videoRetryObserver?.observe(item);

      if (document.visibilityState === 'visible') {
        this.#loadAndPlayVideo(item);
      }
    });

    this.#activeVideoItems.forEach((item) => {
      if (!nextActiveItems.has(item)) {
        this.#videoRetryObserver?.unobserve(item);
        this.#pauseVideoItem(item);
      }
    });

    this.#activeVideoItems = nextActiveItems;
  }

  /**
   * Chỉ chạy video của bố cục đang được hiển thị.
   * Tránh phát trùng video ở gallery desktop/mobile bị ẩn.
   */
  #isRenderedLayoutItem(item) {
    if (item.closest('[hidden]')) return false;
    if (item.getClientRects().length === 0) return false;

    const style = getComputedStyle(item);

    return style.display !== 'none' &&
      style.visibility !== 'hidden';
  }

  #loadAndPlayVideo(item) {
    const deferredMedia = item.querySelector('deferred-media');

    if (!(deferredMedia instanceof HTMLElement)) return;

    let mediaElement = deferredMedia.querySelector('video, iframe');

    if (!mediaElement) {
      if (
        deferredMedia.dataset.danAutoplayLoading === 'true'
      ) {
        return;
      }

      deferredMedia.dataset.danAutoplayLoading = 'true';

      if (typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent(false);
      } else {
        deferredMedia
          .querySelector(
            '[ref="deferredMediaPlayButton"], ' +
            '.deferred-media__poster-button'
          )
          ?.click();
      }

      mediaElement =
        deferredMedia.querySelector('video, iframe');

      if (!mediaElement) {
        this.#scheduleVideoScan();
        return;
      }
    }

    delete deferredMedia.dataset.danAutoplayLoading;

    if (mediaElement instanceof HTMLVideoElement) {
      this.#configureHtmlVideo(mediaElement);

      if (
        typeof deferredMedia.playMedia === 'function'
      ) {
        deferredMedia.playMedia();
      }

      const playPromise = mediaElement.play();

      if (
        playPromise &&
        typeof playPromise.then === 'function'
      ) {
        playPromise
          .then(() => {
            this.#revealAfterFirstFrame(mediaElement);
          })
          .catch(() => {
            /**
             * iOS có thể trì hoãn autoplay khi dữ liệu
             * video chưa tải đủ. Các sự kiện bên dưới
             * sẽ tự yêu cầu phát lại.
             */
          });
      }

      return;
    }

    if (
      typeof deferredMedia.playMedia === 'function'
    ) {
      deferredMedia.playMedia();
    }
  }

  #configureHtmlVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.autoplay = true;
    video.preload = 'auto';

    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('loop', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('preload', 'auto');

    if (
      video.dataset.danVideoConfigured === 'true'
    ) {
      return;
    }

    video.dataset.danVideoConfigured = 'true';

    const retryPlayback = () => {
      if (
        !this.isConnected ||
        document.visibilityState !== 'visible'
      ) {
        return;
      }

      video
        .play()
        .then(() => {
          this.#revealAfterFirstFrame(video);
        })
        .catch(() => {});
    };

    video.addEventListener(
      'loadeddata',
      retryPlayback
    );

    video.addEventListener(
      'canplay',
      retryPlayback
    );

    video.addEventListener(
      'playing',
      () => this.#revealAfterFirstFrame(video)
    );

    /**
     * Yêu cầu trình duyệt tải lại theo preload="auto"
     * ngay sau khi video được chèn vào gallery.
     */
    if (
      video.readyState ===
      HTMLMediaElement.HAVE_NOTHING
    ) {
      video.load();
    }
  }

  /**
   * Chỉ hiện video sau khi khung hình đầu tiên
   * đã sẵn sàng, giúp hạn chế nháy trắng.
   */
  #revealAfterFirstFrame(video) {
    if (
      video.dataset.danVideoReady === 'true'
    ) {
      return;
    }

    const reveal = () => {
      if (!video.isConnected) return;

      video.dataset.danVideoReady = 'true';
    };

    if (
      typeof video.requestVideoFrameCallback ===
      'function'
    ) {
      video.requestVideoFrameCallback(reveal);
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(reveal);
    });
  }

  #pauseVideoItem(item) {
    const deferredMedia =
      item.querySelector?.('deferred-media');

    if (!deferredMedia) return;

    deferredMedia
      .querySelectorAll('video')
      .forEach((video) => video.pause());

    if (
      typeof deferredMedia.pauseMedia === 'function'
    ) {
      deferredMedia.pauseMedia();
    }
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.#activeVideoItems.forEach((item) => {
        this.#loadAndPlayVideo(item);
      });
    } else {
      this.#activeVideoItems.forEach((item) => {
        this.#pauseVideoItem(item);
      });
    }
  };

  /**
   * Thay gallery khi lựa chọn sản phẩm thay đổi.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        const newMediaGallery =
          html.querySelector('media-gallery');

        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[media-gallery] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(
      index,
      event
    );
  }

  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent
      ?.loadHighResolutionImage(
        zoomDialogMedia
      );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    MediaGallery
  );
}
/**
 * Tự động cuộn dải thumbnail để thumbnail đang chọn
 * luôn xuất hiện khi khách vuốt qua ảnh sản phẩm.
 */
const productThumbnailObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type !== 'attributes') continue;

    const thumbnail = mutation.target;

    if (!(thumbnail instanceof HTMLElement)) continue;
    if (!thumbnail.matches('.slideshow-controls__thumbnail')) continue;
    if (thumbnail.getAttribute('aria-selected') !== 'true') continue;
    if (!thumbnail.closest('media-gallery')) continue;
    if (thumbnail.getClientRects().length === 0) continue;

    const container = thumbnail.closest(
      '.slideshow-controls__thumbnails-container'
    );

    if (!(container instanceof HTMLElement)) continue;

    requestAnimationFrame(() => {
      if (!thumbnail.isConnected || !container.isConnected) return;

      const containerRect = container.getBoundingClientRect();
      const thumbnailRect = thumbnail.getBoundingClientRect();

      const canScrollHorizontally =
        container.scrollWidth > container.clientWidth + 1;

      const canScrollVertically =
        container.scrollHeight > container.clientHeight + 1;

      const outsideHorizontalView =
        thumbnailRect.left < containerRect.left ||
        thumbnailRect.right > containerRect.right;

      const outsideVerticalView =
        thumbnailRect.top < containerRect.top ||
        thumbnailRect.bottom > containerRect.bottom;

      if (
        (!canScrollHorizontally || !outsideHorizontalView) &&
        (!canScrollVertically || !outsideVerticalView)
      ) {
        return;
      }

      const reducedMotion = window
        .matchMedia('(prefers-reduced-motion: reduce)')
        .matches;

      const left = canScrollHorizontally
        ? container.scrollLeft +
          (thumbnailRect.left - containerRect.left) -
          (container.clientWidth - thumbnailRect.width) / 2
        : container.scrollLeft;

      const top = canScrollVertically
        ? container.scrollTop +
          (thumbnailRect.top - containerRect.top) -
          (container.clientHeight - thumbnailRect.height) / 2
        : container.scrollTop;

      container.scrollTo({
        left,
        top,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    });
  }
});

productThumbnailObserver.observe(document.documentElement, {
  subtree: true,
  attributes: true,
  attributeFilter: ['aria-selected'],
});